import { LoadingButton } from "@mui/lab";
import { Container, Stack, Typography } from "@mui/material";
import { AxiosError } from "axios";
import { updateClientSettings } from "backend/api/clientSettings";
import { getClientSetting } from "backend/api/emailing";
import { VisitorEmailPolicySelect } from "components/emailing/VisitorEmailPolicySelect/VisitorEmailPolicySelect";
import { VisitorEmailPolicy } from "helpers/constants";
import { toastEmitter } from "helpers/helpers";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "react-query";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "store";
import { LinkStyle } from "./EmailingSettings.style";

export function EmailingSettings() {
  const [selectedVisitorEmailPolicy, setSelectedVisitorEmailPolicy] =
    useState<VisitorEmailPolicy>(VisitorEmailPolicy.InternalAndExternal);

  const connectedClient = useSelector((state: RootState) => state.user.client);

  const { t } = useTranslation("emailing");
  const navigate = useNavigate();

  const { isLoading } = useQuery({
    queryFn: () => getClientSetting(),
    onSuccess: (data) => {
      if (data.clientSettings?.visitorEmailPolicy) {
        setSelectedVisitorEmailPolicy(data.clientSettings.visitorEmailPolicy);
      }
    },
    onError: (error: unknown) => {
      const err = error as AxiosError<{ error?: string }>;
      toastEmitter(
        err.response?.data?.error || t("visitorEmailPolicy.fetchError"),
        "error",
      );
    },
  });

  const { mutate: saveVisitorEmailPolicy, isLoading: isSaving } = useMutation({
    mutationFn: (policy: VisitorEmailPolicy) =>
      updateClientSettings(connectedClient.id, {
        visitor_email_policy: policy,
      }),
    onSuccess: () => {
      toastEmitter(t("visitorEmailPolicy.saveSuccess"), "success");
    },
    onError: (error: unknown) => {
      const err = error as AxiosError<{ error?: string }>;

      toastEmitter(
        err.response?.data?.error || t("visitorEmailPolicy.saveError"),
        "error",
      );
    },
  });

  const onSaveSettings = () => {
    saveVisitorEmailPolicy(selectedVisitorEmailPolicy);
  };

  const onCreateEmailTemplate = () => {
    navigate("/emailing/assign");
  };

  return (
    <Container maxWidth={false}>
      <Stack gap={6}>
        <VisitorEmailPolicySelect
          value={selectedVisitorEmailPolicy}
          onChange={setSelectedVisitorEmailPolicy}
          disabled={isLoading}
          isLoading={isLoading}
        />
        <LinkStyle onClick={onCreateEmailTemplate} color={"error"}>
          {t("visitorEmailPolicy.emailTemplateWarning")}
        </LinkStyle>
        <Stack alignItems={"center"}>
          <LoadingButton
            variant="contained"
            disabled={isSaving || isLoading}
            loading={isSaving}
            onClick={onSaveSettings}
          >
            <Typography variant="button" textTransform={"uppercase"}>
              {t("visitorEmailPolicy.saveSettings")}
            </Typography>
          </LoadingButton>
        </Stack>
      </Stack>
    </Container>
  );
}
